import { useState } from "react";

interface AddSubCategoryModalProps {
    categories: any[];
    isLoading: boolean;
    onClose: () => void;
    onSubmit: (data: {
        productTypeId: string;
        name: string;
        isActive: boolean;
    }) => void;
}

const AddSubCategoryModal = ({
    categories,
    isLoading,
    onClose,
    onSubmit,
}: AddSubCategoryModalProps) => {
    const [name, setName] = useState("");
    const [productTypeId, setProductTypeId] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const validate = () => {
        const newErrors: Record<string, string> = {};
        if (!name.trim()) newErrors.name = "Subcategory name is required";
        if (!productTypeId) newErrors.productTypeId = "Please select a parent category";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;
        onSubmit({ productTypeId, name: name.trim(), isActive });
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-40">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
                <div className="flex items-center justify-between mb-5">
                    <h2 className="font-bold text-lg">Add Subcategory</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        type="button"
                    >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Parent Category */}
                    <div>
                        <label htmlFor="subcat-parent" className="block text-sm font-medium text-gray-700 mb-1">
                            Parent Category
                        </label>
                        <select
                            id="subcat-parent"
                            value={productTypeId}
                            onChange={(e) => setProductTypeId(e.target.value)}
                            className="w-full border rounded-md p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
                        >
                            <option value="">Select a category</option>
                            {categories.map((cat: any) => (
                                <option key={cat._id || cat.id} value={cat._id || cat.id}>
                                    {cat.type}
                                </option>
                            ))}
                        </select>
                        {errors.productTypeId && (
                            <p className="text-red-500 text-xs mt-1">{errors.productTypeId}</p>
                        )}
                    </div>

                    {/* Name */}
                    <div>
                        <label htmlFor="subcat-name" className="block text-sm font-medium text-gray-700 mb-1">
                            Subcategory Name
                        </label>
                        <input
                            id="subcat-name"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Snacks, Beverages"
                            className="w-full border rounded-md p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
                        />
                        {errors.name && (
                            <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                        )}
                    </div>

                    {/* Active toggle */}
                    <div className="flex items-center justify-between">
                        <label htmlFor="subcat-active" className="text-sm font-medium text-gray-700">
                            Active
                        </label>
                        <button
                            id="subcat-active"
                            type="button"
                            role="switch"
                            aria-checked={isActive}
                            onClick={() => setIsActive((prev) => !prev)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-secondary ${isActive ? "bg-green-600" : "bg-gray-300"
                                }`}
                        >
                            <span
                                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${isActive ? "translate-x-5" : "translate-x-1"
                                    }`}
                            />
                        </button>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 rounded-md border text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="px-4 py-2 rounded-md bg-secondary text-white text-sm font-medium hover:bg-opacity-90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                        >
                            {isLoading ? "Creating..." : "Create Subcategory"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddSubCategoryModal;
